import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { IconGlobe } from '~/ui/core/icons';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { usePublishAction } from '../../hooks/use-publish-action';
import { PromptShell } from './PromptShell';
import { ReviewerLeading } from './ReviewerLeading';
import { fireTastefulConfettiAndSettle } from './TastefulConfetti';
import { useReviewerDisplay } from './use-reviewer-display';

type ItemType = 'job-level' | 'job-role';

type PublishPromptProps = {
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: ItemType;
    readonly title: string;
    readonly isNewVersion: boolean;
    readonly onCompareVersions?: () => void;
};

export const PublishPrompt: FunctionComponent<PublishPromptProps> = ({
    architectureId,
    item,
    itemType,
    title,
    isNewVersion,
    onCompareVersions,
}) => {
    const translate = useTranslate();
    const { handlePublish } = usePublishAction({ architectureId, item, itemType });
    const { contextLine } = useReviewerDisplay(item);

    const handlePublishClick = useCallback(
        async (event: { readonly clientX: number; readonly clientY: number }): Promise<void> => {
            await fireTastefulConfettiAndSettle(event);
            await handlePublish();
        },
        [handlePublish],
    );

    const safeTitle = title.trim().length > 0 ? title : translate('pages.job-architecture-v2.prompts.fallback-title');
    const question = isNewVersion
        ? translate('pages.job-architecture-v2.prompts.publish-question-new-version', { title: safeTitle })
        : translate('pages.job-architecture-v2.prompts.publish-question', { title: safeTitle });

    return (
        <PromptShell
            question={question}
            leading={<ReviewerLeading item={item} />}
            contextLine={contextLine}
            actions={
                <>
                    {isNewVersion && onCompareVersions != null && (
                        <Badge variant="gray" onClick={onCompareVersions}>
                            {translate('pages.job-architecture-v2.prompts.compare-versions')}
                        </Badge>
                    )}
                    <Badge variant="success" onClick={handlePublishClick}>
                        <IconGlobe size={11} />
                        {translate('pages.job-architecture-v2.job-role-publish-button')}
                    </Badge>
                </>
            }
        />
    );
};
